import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { useWizardStore } from '@/store/webinar';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { VideoPreview } from '../VideoPreview';
import { VideoSelectionDialog } from '../VideoSelectionDialog';
import { Video, HelpCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useWizardTelemetry } from '@/hooks/useWizardTelemetry';

export function VideoStep() {
  useLanguage();
  const { data, updateNestedData } = useWizardStore();
  const { trackEvent } = useWizardTelemetry();
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasVideo = data.video?.videoId;

  const handleAutoplayToggle = (checked: boolean) => {
    updateNestedData('video', 'autoplay', checked);
    trackEvent('media.autoplay.toggled', { enabled: checked });
  };

  const handleVideoSelect = (video: { id: string; title: string; thumbnailUrl: string; duration: number }) => {
    updateNestedData('video', 'videoId', video.id);
    updateNestedData('video', 'videoTitle', video.title);
    updateNestedData('video', 'videoThumbnail', video.thumbnailUrl);
    updateNestedData('video', 'videoDuration', video.duration);
    trackEvent('media.video.changed', { id: video.id, duration: video.duration });
    setDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">{translateInterfaceText("Escolha o vídeo do seu webinar")}</h2>
        <p className="text-muted-foreground">
          {translateInterfaceText("Selecione um vídeo da sua biblioteca ou faça upload de um novo")}</p>
      </div>

      {/* Autoplay Toggle */}
      <div className="flex items-center justify-end gap-2">
        <Label htmlFor="autoplay" className="text-sm font-medium cursor-pointer">
          {translateInterfaceText("ATIVAR AUTOPLAY")}</Label>
        
        {/* Ícone de ajuda com tooltip */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
                aria-label={translateInterfaceText("Ajuda sobre autoplay")}
                aria-describedby="tooltip-autoplay"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent 
              id="tooltip-autoplay" 
              role="tooltip"
              side="top"
              align="center"
              className="max-w-[280px] bg-[#111] text-white rounded-lg shadow-lg p-3"
            >
              <p className="text-xs leading-relaxed">
                {translateInterfaceText("Inicia o vídeo automaticamente quando o participante entra na sala.")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <Switch
          id="autoplay"
          checked={data.video?.autoplay || false}
          onCheckedChange={handleAutoplayToggle}
        />
      </div>

      {/* Video Preview or Empty State */}
      {hasVideo ? (
        <VideoPreview
          title={data.video.videoTitle || ''}
          thumbnail={data.video.videoThumbnail || ''}
          duration={data.video.videoDuration || 0}
          onChangeVideo={() => setDialogOpen(true)}
        />
      ) : (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <Video className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">{translateInterfaceText("Nenhum vídeo selecionado")}</h3>
          <p className="text-muted-foreground mb-4">
            {translateInterfaceText("Selecione um vídeo da biblioteca ou faça upload de um novo")}</p>
          <Button onClick={() => setDialogOpen(true)}>
            {translateInterfaceText("Selecionar Vídeo")}</Button>
        </div>
      )}


      {/* Validation Alert */}
      {!hasVideo && (
        <Alert>
          <AlertDescription>
            {translateInterfaceText("Você precisa selecionar um vídeo para continuar")}</AlertDescription>
        </Alert>
      )}

      {/* Video Selection Dialog */}
      <VideoSelectionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSelectVideo={handleVideoSelect}
      />
    </div>
  );
}
